import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  CheckBox,
  ConfirmationModal,
  Input,
  Modal,
  MultiSelectDropdown,
} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {getCountryOptions} from '@/constants/formatters';
import {useCountryList} from '@/context/CountryListContext';
import type {UpdateSubscriptionPlanDTO} from '@/services/subscriptionPlanService';
import type {AfriexCountryCode} from '@/types';
import {mapCountryListToOptions} from '@/utils/countries';

import type {UpdatePlanModalProps} from './types';

export default function UpdatePlanModal({
  isOpen,
  label,
  onClose,
  errors,
  loading,
  onSubmit,
  onDelete,
  selectedPlan,
}: UpdatePlanModalProps & {
  onDelete: (id: string) => Promise<void>;
  onSubmit: ({
    id,
    form,
  }: {
    id: string;
    form: UpdateSubscriptionPlanDTO;
  }) => Promise<void>;
}) {
  const [form, setForm] = useState<UpdateSubscriptionPlanDTO>({
    name: selectedPlan?.name || '',
    description: selectedPlan?.description || '',
    amount: selectedPlan?.amount || 0,
    countries: selectedPlan?.countries ?? [],
    isActive: selectedPlan?.isActive || false,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;

  const countryOptions = mapCountryListToOptions(countryList);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const {target} = e;
    const {name, value} = target;
    if (name === 'features') {
      setForm((prev) => ({
        ...prev,
        features: [value],
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      if (!selectedPlan) {
        throw new Error('Cannot submit plan update without a selected plan');
      }
      await onSubmit({id: selectedPlan.id, form});
    } catch (err) {
      console.error(err);
    }
  };

  const handleConfirmDelete = async () => {
    await onDelete(String(selectedPlan?.id));
    setShowDeleteConfirm(false);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  // Pure comparison of the working form against the plan it was seeded from,
  // so it is derived during render. With no plan there is nothing to diff, and
  // the button stays disabled, which is what the effect's initial state did.
  const hasFormDataChanged = selectedPlan
    ? form.name !== (selectedPlan.name || '') ||
      form.description !== (selectedPlan.description || '') ||
      form.amount !== (selectedPlan.amount || 0) ||
      form.isActive !== (selectedPlan.isActive || false) ||
      JSON.stringify(form.countries) !==
        JSON.stringify(selectedPlan.countries ?? [])
    : false;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
        title={label}
        closeOnOutsideClick
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {[
            {name: 'name', type: 'text', label: 'Plan Name', component: Input},
            {
              name: 'description',
              type: 'text',
              label: 'Description',
              component: Input,
            },
            {
              name: 'amount',
              type: 'number',
              label: 'Amount (USD)',
              component: Input,
            },
          ].map(({name, type, label, component: Component}) => (
            <div key={name}>
              <Component
                label={label}
                type={type}
                name={name}
                value={form[name as keyof typeof form] as string | number}
                onChange={handleChange}
                className="w-full"
                placeholder={`Enter ${label}`}
                error={errors[name as keyof typeof errors]}
              />
            </div>
          ))}

          {/* Countries */}
          <div className="">
            <MultiSelectDropdown
              label="Countries"
              name="countries"
              onChange={(p: unknown[]) => {
                const countries = p as AfriexCountryCode[];
                setForm((prev) => ({
                  ...prev,
                  countries,
                }));
              }}
              options={getCountryOptions(countryOptions)}
              prompt="Select countries"
              error={errors.countries}
              value={form.countries}
            />
          </div>

          {/* Toggle */}
          <div className="">
            <CheckBox
              label="Mark to make this plan active"
              type="checkbox"
              checked={form.isActive}
              // `CheckBox` takes `InputProps`, which carries a
              // `[rest: string]: any` index signature, so the handler argument
              // has to be annotated to be typed at all.
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setForm((prev) => ({
                  ...prev,
                  isActive: e.target.checked,
                }));
              }}
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            {selectedPlan?.isActive ? (
              <PermissionsProvider
                permission={AfriexAdminPermission.SubscriptionPlanManage}
              >
                <Button
                  variant="outline"
                  colorScheme="red"
                  className="mr-auto"
                  disabled={loading}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Deactivate
                </Button>
              </PermissionsProvider>
            ) : null}
            <Button
              type="submit"
              className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
              disabled={loading || !hasFormDataChanged}
            >
              {loading ? 'Submitting...' : 'Update Plan'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Deactivate Plan"
        label={`Deactivate plan ${selectedPlan?.name ?? ''}? It will be marked inactive.`}
        confirmActionLabel="Deactivate"
        cancelActionLabel="Cancel"
        loadingLabel="Deactivating..."
        loading={loading}
        confirmAction={handleConfirmDelete}
        cancelAction={() => setShowDeleteConfirm(false)}
      />
    </>
  );
}
